/**
 * ShoppingCart -> Rezervasyon dönüştürücü
 *
 * - Her oda ayrı bir "kalem" olarak üretilir.
 * - "adresler" dizisindeki her eleman, C# tarafındaki Adres class'ının alanlarıyla birebir eşleşir.
 *
 * Kullanım:
 *   const { shoppingCartToRezervasyon } = require('./shoppingCartToRezervasyon');
 *   const rezervasyon = shoppingCartToRezervasyon(shoppingCartResponse, { cariKod: 'GERCEK_CARI_KODU' });
 */

/**
 * @param {Object} shoppingCartResponse - API'den dönen { ShoppingCart: {...}, HasError: false } yapısı
 * @param {Object} [options]
 * @param {string} [options.cariKod] - Hedef sistemdeki cari kod. ShoppingCart içinde bu bilgi
 *   yok, dışarıdan (acente/context bilgisinden) verilmesi gerekiyor. Verilmezse "DENEME" kullanılır.
 * @returns {{ Rezervasyon: Object }}
 */
function shoppingCartToRezervasyon(shoppingCartResponse, options = {}) {
  const cart = shoppingCartResponse.ShoppingCart;
  const cariKod = options.cariKod || 'DENEME';

  if (!cart || !Array.isArray(cart.HotelBookings) || cart.HotelBookings.length === 0) {
    throw new Error('ShoppingCart içinde HotelBookings bulunamadı.');
  }

  const firstBooking = cart.HotelBookings[0];

  // cart.Passengers, tüm booking'lerdeki PassengerDetails ile AYNI SIRADA olan, daha zengin
  // (Email, Phone, PassportNo, Address vb.) bir yolcu listesi. Sıra hizası ShoppingCart
  // örneklerinde tutarlı görünüyor; farklı bir sıralama gözlemlerseniz PaxReferanceId üzerinden
  // eşleştirme yapmak daha güvenli olur.
  const cartPassengers = cart.Passengers || [];

  const adresler = [];
  const kalemler = [];
  // Her kalem için oda tipi arama/oluşturma sırasında lazım olacak yetişkin/çocuk sayıları.
  // kalemler ile aynı sırada (index bazında eşleşiyor).
  const odaTipiIhtiyaclari = [];
  let globalIndex = 0;

  cart.HotelBookings.forEach(booking => {
    const passengers = booking.PassengerDetails || [];

    // Bu booking'e ait yolcuları adresler dizisine ekleyip global index'lerini kaydediyoruz
    // (kalem.kisiSira, adresler dizisindeki index'lere referans veriyor).
    const indexedPassengers = passengers.map(p => {
      const idx = globalIndex;
      const cartPassenger = cartPassengers[idx] || {};
      globalIndex++;

      adresler.push(mapAdres(p, cartPassenger, booking));

      // PaxRoomNo, Adres modelinde yok; sadece burada oda bazlı gruplama için kullanılıyor.
      return { ...p, __globalIndex: idx };
    });

    // Yolcuları oda numarasına göre grupla (ilk görülme sırası korunur)
    const roomsMap = new Map();
    indexedPassengers.forEach(p => {
      if (!roomsMap.has(p.PaxRoomNo)) roomsMap.set(p.PaxRoomNo, []);
      roomsMap.get(p.PaxRoomNo).push(p);
    });

    const roomNumbers = Array.from(roomsMap.keys());
    const roomCount = roomNumbers.length || 1;

    // Her oda için ayrı bir kalem üret
    roomNumbers.forEach(roomNo => {
      const roomPassengers = roomsMap.get(roomNo);
      kalemler.push(mapKalem(booking, roomPassengers, roomCount));

      // Yetişkin/Çocuk/Bebek kırılımı doğum tarihinden hesaplanıyor (ShoppingCart'ta
      // PasengerInfo[].Child alanı bebek/çocuk ayrımı yapmıyor, sadece toplam "çocuk" veriyor).
      // Eşik: 12 yaş üstü yetişkin, 2-11 yaş çocuk (Cocuk1), 2 yaş altı bebek (Cocuk2).
      let yetiskin = 0;
      let cocuk1 = 0;
      let cocuk2 = 0; // Cocuk2 = bebek

      roomPassengers.forEach(p => {
        const yas = calculateAge(p.BirthDate, booking.StartDate);
        if (yas < 2) cocuk2++;
        else if (yas < 12) cocuk1++;
        else yetiskin++;
      });

      odaTipiIhtiyaclari.push({ yetiskin, cocuk1, cocuk2 });
    });
  });

  return {
    Rezervasyon: {
      kayitTarihi: turkishDateTimeToIsoUtc(firstBooking.BookingTimeTable.BookingDate),
      karsiRezKod: firstBooking.BookingCode,
      ShoppingCardId: cart.Id,
      MisafirIsmiKontrolEdilmesin: true,
      cariKod: { kod: cariKod },
      adresler,
      kalemler
    },
    odaTipiIhtiyaclari
  };
}

/**
 * "01.07.2026 14:35:45" formatındaki Türkiye saatini (UTC+3 kabul edilir)
 * ISO 8601 UTC string'e çevirir.
 */
function turkishDateTimeToIsoUtc(trDateStr) {
  const [datePart, timePart] = trDateStr.split(' ');
  const [day, month, year] = datePart.split('.').map(Number);
  const [hour, minute, second] = timePart.split(':').map(Number);
  const utcMs = Date.UTC(year, month - 1, day, hour - 3, minute, second);
  return new Date(utcMs).toISOString();
}

/** ISO tarih/tarih-saat string'inden sadece "YYYY-MM-DD" kısmını döner. */
function dateOnly(isoOrDateStr) {
  return isoOrDateStr.split('T')[0];
}

/**
 * `value ?? fallback` yerine kullanılan yardımcı: sadece null/undefined ise fallback döner,
 * 0/false/'' gibi geçerli falsy değerleri korur. (Projede ?? / ?. desteklenmiyor.)
 */
function orDefault(value, fallback) {
  return (value === null || value === undefined) ? fallback : value;
}

function round2(n) {
  return Math.round(n * 100) / 100;
}

/**
 * "Male"/"Female" (cart.Passengers) veya "0"/"1" (PassengerDetails) gender değerlerini
 * "E"/"K" formatına çevirir. Tanınmayan bir değer gelirse olduğu gibi döner.
 */
function genderToCinsiyet(gender) {
  if (gender === 'Male' || gender === '0' || gender === 0) return 'E';
  if (gender === 'Female' || gender === '1' || gender === 1) return 'K';
  return gender || null;
}

/** C# tarafındaki default DateTime ("0001-01-01T00:00:00") değerini "set edilmemiş" kabul eder. */
function isValidDate(dateStr) {
  return !!dateStr && !dateStr.startsWith('0001-01-01');
}

/**
 * Doğum tarihinden, referans tarihine (varsayılan: check-in tarihi) göre yaş hesaplar.
 */
function calculateAge(birthDateStr, referenceDateStr) {
  if (!birthDateStr) return 0;
  const birth = new Date(birthDateStr);
  const ref = referenceDateStr ? new Date(referenceDateStr) : new Date();
  let age = ref.getFullYear() - birth.getFullYear();
  const monthDiff = ref.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && ref.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

/**
 * Tek bir yolcuyu Adres modeline çevirir.
 * @param {Object} passengerDetail - booking.PassengerDetails elemanı (oda no, doğum tarihi vb.)
 * @param {Object} cartPassenger - cart.Passengers elemanı (iletişim/pasaport bilgileri vb.)
 * @param {Object} booking - içinde bulunduğu HotelBookings elemanı (yaş hesaplama referans tarihi için)
 */
function mapAdres(passengerDetail, cartPassenger, booking) {
  const ad = passengerDetail.Name || cartPassenger.FirstName || '';
  const soyad = passengerDetail.SurName || cartPassenger.LastName || '';

  return {
    id: 0,        // yeni kayıt; backend atayacak
    kod: 0,        // ShoppingCart'ta karşılığı yok
    profilId: 0,   // ShoppingCart'ta karşılığı yok
    sicilId: 0,    // ShoppingCart'ta karşılığı yok
    // KisiTip enum değerleri bilinmediğinden 0 bırakıldı. Gerekirse burada
    // passengerDetail.PaxType (0 = yetişkin) üzerinden kendi enum'unuza eşleyin.
    kisiTip: passengerDetail.PaxType || 0,
    adSoyad: `${ad} ${soyad}`.trim(),
    telefon1: null,
    gsm: cartPassenger.Phone || null,
    eMail: cartPassenger.Email || null,
    tcKimlikNo: passengerDetail.NationalId || cartPassenger.CitizenNo || null,
    uyeNo: null,   // ShoppingCart'ta karşılığı yok
    adres: cartPassenger.Address || null,
    dogumTarihi: passengerDetail.BirthDate, // zaten "YYYY-MM-DD" formatında
    ulke: cartPassenger.AddressCountry || cartPassenger.Nationality || null,
    cinsiyet: genderToCinsiyet(orDefault(cartPassenger.Gender, passengerDetail.Gender)),
    kisiSira: orDefault(passengerDetail.PassengerID, 0),
    kisiTableRef: 0, // ShoppingCart'ta karşılığı yok
    yas: calculateAge(passengerDetail.BirthDate, booking.StartDate),
    firstName: ad,
    lastName: soyad,
    disID: 0, // DisID enum değerleri bilinmediğinden 0 bırakıldı
    pasaportNo: cartPassenger.PassportNo || null,
    pasaportVerilisYeri: null, // ShoppingCart'ta karşılığı yok
    pasaportVerilisTarihi: null, // ShoppingCart'ta sadece bitiş tarihi var, veriliş tarihi yok
    pasaportBitisTarihi: isValidDate(cartPassenger.PassportExpireDate)
      ? dateOnly(cartPassenger.PassportExpireDate)
      : null,
    tekerlekliSandalye: orDefault(cartPassenger.WheelChairService, 0),
    mileCard: cartPassenger.FrequentFlayerNo || null
  };
}

/**
 * turOperatoru gibi tam bir Satici kaydı (SaticiAra'nın döndürdüğü şekil) bekleyen alanlar için
 * boş/varsayılan şablon üretir. kod/isim önceden doldurulup rezervasyonuKaydet.js'deki
 * SaticiAra/SaticiKayit akışında kullanılır.
 */
function emptySaticiKaydi(kod, isim) {
  return {
    YurtIci_YurtDisi: 0,
    CariKod: null,
    KDV: 0.0,
    Yer: null,
    Ulke: null,
    Sehir: null,
    SaticiKonumu: null,
    Web: null,
    Tel1: null,
    Tel2: null,
    Fax: null,
    Teleks: null,
    EMail: null,
    GSM: null,
    Adres: null,
    id: '',
    kod: kod || '',
    isim: isim || '',
    tip: 0,
    intField: null,
    tumSirketler: false,
    sayfaAdi: null
  };
}

/**
 * Tek bir oda için kalem oluşturur.
 * @param {Object} booking - HotelBookings dizisindeki eleman
 * @param {Array} roomPassengers - bu odaya ait, __globalIndex eklenmiş yolcular
 * @param {number} roomCount - booking'deki toplam oda sayısı (fiyatı eşit bölüştürmek için)
 */
function mapKalem(booking, roomPassengers, roomCount) {
  const kisiSayisi = roomPassengers.length;
  // NOT: Bu "kisiSira", adresler dizisindeki index'lere referans veren bir dizi.
  // Adres modelindeki kişiye özel "kisiSira" (PassengerID) alanıyla karıştırılmamalı.
  const kisiSira = roomPassengers.map(p => p.__globalIndex);

  // NOT: ShoppingCart, oda bazlı ayrı bir fiyat kırılımı vermiyor (RoomPriceDetaiList
  // tüm rezervasyon için tek toplam içeriyor). Bu yüzden TotalFare / ServicePrice,
  // odalara eşit olarak bölüştürülüyor. Gerçek oda bazlı fiyat farklıysa bu kısmı
  // kendi fiyat kırılımınıza göre güncellemeniz gerekir.
  const satisPerRoom = round2(booking.TotalFare / roomCount);
  const alisPerRoom = round2(booking.ServicePrice / roomCount);

  return {
    saticiTip: 'Otel',
    entTip: 3,
    entId: booking.BookingCode,
    islemRef: 0,
    donem1: dateOnly(booking.StartDate),
    donem2: dateOnly(booking.EndDate),
    odeyenTaraf: 'acente',
    aciklama: '',
    tedarikciAciklama: `${booking.Provider} Otel Satışı`,
    entegrasyonKodu: 0,
    komisyonYuzdeTip: 0,
    komisyonYuzde: 0,
    komisyonTutar: 0,
    P_RP: 'R',
    satis: { KDVOran: 0, paraBirimi: booking.Currency, kur: 1 },
    alis: { KDVOran: 0, paraBirimi: booking.Currency, kur: 1 },
    komisyon: { KDVOran: 0, NetTutar: 0, paraBirimi: booking.Currency, kur: 1 },
    acenteKomisyon: null,
    iskonto: { oran: 0, tutar: 0, iskontoSebebi: null },
    // NOT: turOperatoru burada tam bir Satici kaydı şablonuyla, kod/isim = booking.Provider
    // olarak dolduruluyor. rezervasyonuKaydet.js akışı bunu SaticiAra/SaticiKayit (tip: TurOperatoru)
    // ile gerçek satıcı kaydıyla değiştirir.
    turOperatoru: emptySaticiKaydi(booking.Provider, booking.Provider),
    departman: { id: null, kod: null, isim: null },
    kdvID: { id: null, kod: null, isim: null },
    kdvMuafiyetID: { id: null, kod: null, isim: null },

    yet_Satis: satisPerRoom,
    co1_Satis: 0,
    co2_Satis: 0,
    co3_Satis: 0,
    co4_Satis: 0,
    ekY_Satis: 0,

    yet_Alis: alisPerRoom,
    co1_Alis: 0,
    co2_Alis: 0,
    co3_Alis: 0,
    co4_Alis: 0,
    ekY_Alis: 0,

    yet_KisiSayisi: kisiSayisi,
    co1_KisiSayisi: 0,
    co2_KisiSayisi: 0,
    co3_KisiSayisi: 0,
    co4_KisiSayisi: 0,
    ekY_KisiSayisi: 0,

    // NOT: satici.id/kod burada geçici olarak otel bilgisinden dolduruluyor;
    // rezervasyonuKaydet.js akışı bunu SaticiAra/SaticiKayit ile gerçek değerlerle güncelliyor.
    satici: {
      id: '-1',
      kod: booking.ServiceHotelID || String(booking.ArkmanHotelID),
      isim: booking.HotelName,
      tip: 1
    },

    alisPara: { paraBirimi: 'TL', kur: 1 },
    satisPara: { paraBirimi: 'TL', kur: 1 },
    komisyonPara: { paraBirimi: 'TL', kur: 1 },
    masrafMerkezi: { id: null, kod: null, isim: null },

    kalemOtel: {
      // NOT: odaTipi burada sadece isim (booking.RateName) ile dolduruluyor.
      // rezervasyonuKaydet.js akışı bunu OdaTipleriListe/OdaTipiKayit ile gerçek kod'a çevirir.
      odaTipi: { id: null, kod: null, isim: booking.RateName || null },
      fiyatKodu: { id: null, kod: null, isim: null },
      pansiyon: 0,
      girisSaati: dateOnly(booking.StartDate),
      konum: null,
      RP: 'T',
      odaSayi: 1,
      kontenjan: false,
      freeKisi: 0
    },

    maliyetCariKod: { kod: '' },
    Alis_RPTF: 'T',
    kisiIsimList: [],
    kisiSira
  };
}

export { shoppingCartToRezervasyon };
